import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Toast } from '../ui';
import Drawer from './Drawer';
import { getProductTags, listTagGroups, setProductTags } from '../../services/tagsApi';
import './pim.css';

/**
 * Sidebar panel for the product editor: the product's tags, grouped by
 * dimension, with a picker.
 *
 * Only groups that target products are offered — the vocabulary is shared with
 * customers, and a customer-only dimension has no meaning here. Tags are flat
 * and cross-entity, which is what separates them from categories (hierarchical,
 * product-only) and attributes (scoped by channel and locale).
 */
export default function ProductTagsPanel({ sku }) {
  const [groups, setGroups] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [vocab, current] = await Promise.all([
        listTagGroups({ entity_type: 'product' }),
        getProductTags(sku),
      ]);
      setGroups(vocab || []);
      setAssigned(current.tags || []);
    } catch (e) {
      // The product may not be in the PIM yet; keep the panel quiet.
      setAssigned([]);
    }
  }, [sku]);

  useEffect(() => {
    load();
  }, [load]);

  const openPicker = () => {
    setSelected(new Set(assigned.map((t) => t.id)));
    setPicking(true);
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setProductTags(sku, Array.from(selected));
      setPicking(false);
      setToast({ message: 'Tags updated' });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Failed to save tags', error: true });
    } finally {
      setBusy(false);
    }
  };

  const hasVocabulary = groups.some((g) => (g.values || []).length > 0);

  return (
    <>
      <Card
        title="Tags"
        actions={
          hasVocabulary && (
            <Button size="sm" variant="ghost" onClick={openPicker}>
              Edit
            </Button>
          )
        }
      >
        {!hasVocabulary && (
          <p className="pim-muted" style={{ margin: 0, fontSize: 12 }}>
            No product tags configured yet — add them in Configuration → Tags.
          </p>
        )}
        {hasVocabulary && assigned.length === 0 && (
          <p className="pim-muted" style={{ margin: 0, fontSize: 12 }}>
            Not tagged
          </p>
        )}
        {assigned.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {assigned.map((tag) => (
              <span key={tag.id} title={`${tag.group_label} · ${tag.code}`}>
                <Badge variant="info">{tag.label}</Badge>
              </span>
            ))}
          </div>
        )}
      </Card>

      {picking && (
        <Drawer
          title="Tags"
          sub={`Which tags apply to ${sku}?`}
          onClose={() => setPicking(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPicking(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy}>Save ({selected.size})</Button>
            </>
          }
        >
          {groups.map((group) => (
            <div key={group.id} style={{ marginBottom: 16 }}>
              <strong style={{ fontSize: 13 }}>{group.label}</strong>
              {(group.values || []).length === 0 ? (
                <p className="pim-muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  No values in this group yet.
                </p>
              ) : (
                group.values.map((value) => (
                  <label
                    key={value.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(value.id)}
                      onChange={() => toggle(value.id)}
                    />
                    <span style={{ flex: 1 }}>{value.label}</span>
                    <code style={{ fontSize: 11, color: '#99a' }}>{value.code}</code>
                  </label>
                ))
              )}
            </div>
          ))}
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
